import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const PatientNewsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Patient News';
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  const categoryDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // Mock news data
  const newsData = [
    {
      id: 1,
      title: 'New Telehealth Services Available',
      excerpt: 'We are excited to announce the launch of our new telehealth services, making healthcare more accessible than ever.',
      image: '/news-image-1.jpg',
      category: 'Services'
    },
    {
      id: 2,
      title: 'COVID-19 Safety Protocols Updated',
      excerpt: 'Updated safety protocols and guidelines for in-person appointments during the ongoing pandemic.',
      image: '/news-image-2.jpg',
      category: 'Safety'
    },
    {
      id: 3,
      title: 'New Specialist Joins Our Team',
      excerpt: 'Dr. Sarah Johnson joins our cardiology department, bringing over 15 years of experience.',
      image: '/news-image-3.jpg',
      category: 'Team'
    },
    {
      id: 4,
      title: 'Digital Health Records Implementation',
      excerpt: 'Our new digital health records system is now live, providing better patient care coordination.',
      image: '/news-image-4.jpg',
      category: 'Technology'
    },
    {
      id: 5,
      title: 'Preventive Care Campaign Launch',
      excerpt: 'Join our new preventive care campaign focused on early detection and wellness.',
      image: '/news-image-5.jpg',
      category: 'Wellness'
    },
    {
      id: 6,
      title: 'Emergency Services Expansion',
      excerpt: 'We have expanded our emergency services to provide 24/7 care for urgent medical needs.',
      image: '/news-image-6.jpg',
      category: 'Services'
    },
    {
      id: 7,
      title: 'Mental Health Awareness Month',
      excerpt: 'Supporting mental health awareness with special programs and resources for patients.',
      image: '/news-image-7.jpg',
      category: 'Wellness'
    },
    {
      id: 8,
      title: 'New Medical Equipment Arrival',
      excerpt: 'State-of-the-art medical equipment has been installed to improve diagnostic capabilities.',
      image: '/news-image-8.jpg',
      category: 'Technology'
    },
    {
      id: 9,
      title: 'Patient Portal Enhancements',
      excerpt: 'Our patient portal has been upgraded with new features for better communication.',
      image: '/news-image-9.jpg',
      category: 'Technology'
    },
    {
      id: 10,
      title: 'Community Health Fair',
      excerpt: 'Join us for our annual community health fair featuring free screenings and health education.',
      image: '/news-image-10.jpg',
      category: 'Events'
    },
    {
      id: 11,
      title: 'Pediatric Care Updates',
      excerpt: 'Enhanced pediatric care services with specialized programs for children of all ages.',
      image: '/news-image-11.jpg',
      category: 'Services'
    },
    {
      id: 12,
      title: 'Nutrition and Diet Programs',
      excerpt: 'New nutrition counseling and diet programs to support healthy lifestyle choices.',
      image: '/news-image-12.jpg',
      category: 'Wellness'
    },
    {
      id: 13,
      title: 'Senior Care Services',
      excerpt: 'Comprehensive senior care services designed to meet the unique needs of older adults.',
      image: '/news-image-13.jpg',
      category: 'Services'
    },
    {
      id: 14,
      title: 'Research Study Opportunities',
      excerpt: 'Participate in cutting-edge medical research studies and contribute to medical advancements.',
      image: '/news-image-14.jpg',
      category: 'Research'
    },
    {
      id: 15,
      title: 'Insurance and Billing Updates',
      excerpt: 'Important updates regarding insurance coverage and billing procedures for all patients.',
      image: '/news-image-15.jpg',
      category: 'Administrative'
    }
  ];

  const handleReadMore = (newsId) => {
    // In a real app, this would navigate to the full article
    console.log('Reading full article:', newsId);
  };

  const handleAddCategory = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
    setShowSortDropdown(false);
  };

  const handleAddSortOption = () => {
    setShowSortDropdown(!showSortDropdown);
    setShowCategoryDropdown(false);
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowCategoryDropdown(false);
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
  };

  const handleSortRemove = () => {
    setSortBy('');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Left Sidebar - 20% */}
        <div className="w-1/5 bg-gray-100 min-h-screen p-6">
          <div className="space-y-6">
            {/* Search Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search:
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

                         {/* Filter Category Section */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Filter Category:
               </label>
               <div className="relative" ref={categoryDropdownRef}>
                 <div className="flex items-center border border-gray-300 rounded-md bg-white">
                   <div className="flex-1 px-3 py-2 min-h-[40px] flex flex-wrap gap-1 items-center">
                     {selectedCategories.length === 0 ? (
                       <span className="text-gray-400 text-sm">Please Select</span>
                     ) : (
                       selectedCategories.map((category) => (
                         <span
                           key={category}
                           className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                         >
                           {category}
                           <button
                             onClick={() => handleCategoryRemove(category)}
                             className="text-blue-600 hover:text-blue-800"
                           >
                             <X className="h-3 w-3" />
                           </button>
                         </span>
                       ))
                     )}
                   </div>
                   <button
                     onClick={handleAddCategory}
                     className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                     title="Add Category"
                   >
                     <Plus className="h-4 w-4" />
                   </button>
                 </div>
                 
                 {/* Category Dropdown */}
                 {showCategoryDropdown && (
                   <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                     {['Services', 'Safety', 'Team', 'Technology', 'Wellness', 'Events', 'Research', 'Administrative'].map((category) => (
                       <button
                         key={category}
                         onClick={() => handleCategorySelect(category)}
                         className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                         disabled={selectedCategories.includes(category)}
                       >
                         {category}
                       </button>
                     ))}
                   </div>
                 )}
               </div>
             </div>

                         {/* Sort By Section */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Sort By:
               </label>
               <div className="relative" ref={sortDropdownRef}>
                 <div className="flex items-center border border-gray-300 rounded-md bg-white">
                   <div className="flex-1 px-3 py-2 min-h-[40px] flex items-center">
                     {sortBy === '' ? (
                       <span className="text-gray-400 text-sm">Sort Options</span>
                     ) : (
                       <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                         {sortBy}
                         <button
                           onClick={handleSortRemove}
                           className="text-blue-600 hover:text-blue-800"
                         >
                           <X className="h-3 w-3" />
                         </button>
                       </span>
                     )}
                   </div>
                   <button
                     onClick={handleAddSortOption}
                     className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                     title="Add Sort Option"
                   >
                     <Plus className="h-4 w-4" />
                   </button>
                 </div>
                 
                 {/* Sort Dropdown */}
                 {showSortDropdown && (
                   <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                     {[
                       { value: 'date-newest', label: 'Date (Newest First)' },
                       { value: 'date-oldest', label: 'Date (Oldest First)' },
                       { value: 'title-az', label: 'Title (A-Z)' },
                       { value: 'title-za', label: 'Title (Z-A)' },
                       { value: 'category', label: 'Category' }
                     ].map((option) => (
                       <button
                         key={option.value}
                         onClick={() => handleSortSelect(option.label)}
                         className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                       >
                         {option.label}
                       </button>
                     ))}
                   </div>
                 )}
               </div>
             </div>
          </div>
        </div>

        {/* Right Content Area - 80% */}
        <div className="w-4/5 bg-gray-100 p-6">
          <div className="grid grid-cols-5 gap-4">
            {newsData.map((news) => (
              <div key={news.id} className="bg-white border border-blue-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                 {/* News Image */}
                 <div className="h-32 bg-gray-200 flex items-center justify-center overflow-hidden">
                   <img
                     src="/medical_news.jpg"
                     alt="Medical News"
                     className="w-full h-full object-cover"
                     onError={(e) => {
                       e.target.style.display = 'none';
                       e.target.nextSibling.style.display = 'flex';
                     }}
                   />
                   <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center hidden">
                     <div className="text-center">
                       <div className="w-16 h-16 bg-blue-300 rounded-full mx-auto mb-2 flex items-center justify-center">
                         <span className="text-blue-600 text-xs font-medium">MED</span>
                       </div>
                       <span className="text-blue-600 text-xs">Medical Scene</span>
                     </div>
                   </div>
                 </div>
                
                {/* News Content */}
                <div className="p-3">
                  <h3 className="text-blue-600 font-semibold text-sm mb-2 line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 text-xs mb-3 line-clamp-3">
                    {news.excerpt}
                  </p>
                  <button
                    onClick={() => handleReadMore(news.id)}
                    className="w-full bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded hover:bg-blue-700 transition-colors"
                  >
                    Read More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 h-16"></div>
    </div>
  );
};

export default PatientNewsPage; 